import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import React from "react";
import tinycolor from "tinycolor2";

export const COLORS = [
    "#DCE774", // yellow
    "#BA68C8", // lila
    "#FF8A65", // orange
    // "#687689", //gray

    "#36D67A", // green
    "#2CCCE4", // blue
    "#F47373", // red
    // "#D8C1A2", // brown
];

function getRandEl<T>(arr: T[]): T | undefined {
    if (arr.length === 0) return undefined;
    const randomIndex = Math.floor(Math.random() * arr.length);
    return arr[randomIndex];
}

function getRandFreeColor(arr: string[], selectedColors: string[]): string {
    const availableColors = arr.filter(c => !selectedColors.includes(c));
    return getRandEl(availableColors) ?? "#000000";
}

// Sums up an array of color strings, weighting each the same.
// Return the result as hex string to make usage with other things easier.
export const mixColors = (colors: string[]) => {
    // Sum up every value in the array.
    let sum = tinycolor("#000000").toRgb();

    // Fun fact: this is an impure function call because it references sum.
    // Calling reduce instead would be pure, but it doesn't matter here.
    colors.forEach((element) => {
        let rgb = tinycolor(element).toRgb();
        sum.r += rgb.r;
        sum.g += rgb.g;
        sum.b += rgb.b;
    });

    sum.r /= colors.length;
    sum.g /= colors.length;
    sum.b /= colors.length;

    return tinycolor(sum).toHexString();
};

// Create a nested object state to reuse logic for alice and bob individually
export interface Person {
    secret: string;
    mixed: string;
    received: string;
    result: string;
    hasStarted: boolean;
    hasPicked: boolean;
    hasMixed: boolean;
    hasSent: boolean;
    hasReceived: boolean;
    hasResult: boolean;
};
export interface MsgProgress {
    visibleMsgCount: number;
    generatorShown: {
        alice: boolean;
        bob: boolean;
    };
    secretsShown: boolean;
    mixBtnsShown: boolean;
    hasOtherMix: {
        alice: boolean;
        bob: boolean;
    };
    resultsShown: boolean;
    eveKnowledgeGuessedRight: boolean;
    eveFailedBruteforce: boolean;
    popup: {
        icon: React.ReactNode;
        title: string;
        children: React.ReactNode;
        timerDuration?: number;
        activateConfetti?: boolean;
    } | null; // null = no popup shown
}
export interface MixState {
    generator: string;
    bob: Person;
    alice: Person;
    selectedColors: string[];
    msgProgress: MsgProgress;
};
export type Target = "bob" | "alice";

// Initial values to (re-)set the reducer
export const initialColor = "white";
export const initialPerson: Person = {
    secret: initialColor,
    mixed: initialColor,
    received: initialColor,
    result: initialColor,
    hasStarted: false,
    hasPicked: false,
    hasMixed: false,
    hasSent: false,
    hasReceived: false,
    hasResult: false,
};
export const initialMsgProgress = {
    visibleMsgCount: 1,
    generatorShown: {
        alice: false,
        bob: false,
    },
    secretsShown: false,
    mixBtnsShown: false,
    hasOtherMix: {
        alice: false,
        bob: false,
    },
    resultsShown: false,
    eveKnowledgeGuessedRight: false,
    eveFailedBruteforce: false,
    popup: null
}
export const initialMixState: MixState = {
    generator: initialColor,
    bob: { ...initialPerson },
    alice: { ...initialPerson },
    selectedColors: [], // will get filled by ColorPickers and FieldGenerator
    msgProgress: { ...initialMsgProgress }
};

function propagateUpdates(state: MixState, target?: Target) {
    const targets: Target[] = ["alice", "bob"];

    const updateMix = (t: Target) => {
        if (state[t].hasMixed) {
            state[t].mixed = mixColors([state.generator, state[t].secret]);
        }
    };

    const updateReceived = (t: Target) => {
        if (state[t].hasSent) {
            const other: Target = t === "bob" ? "alice" : "bob";
            state[other].received = state[t].mixed;
        }
    };

    const updateResult = () => {
        targets.forEach((t) => {
            if (state[t].hasResult) {
                state[t].result = mixColors([
                    state.generator,
                    state.bob.secret,
                    state.alice.secret,
                ]);
            }
        });
    };

    if (!target) {
        // Generator updated
        targets.forEach((t) => {
            updateMix(t);
            updateReceived(t);
        });
        updateResult();
    } else {
        // Person updated
        updateMix(target);
        updateReceived(target);
        updateResult();
    }
}

export const mixSlice = createSlice({
    name: "mixer",
    initialState: initialMixState,
    reducers: {
        // ONLY FOR MIXCHATSCREEN -----------------

        // update provided simple (not nested!) subset of MsgProgress states
        updateProgress: (state, action: PayloadAction<Partial<MsgProgress>>) => {
            const msgProgress = action.payload;
            state.msgProgress = {
                ...state.msgProgress,
                ...msgProgress // only overwrite new ones provided
            }
        },
        unpick: (state, action: PayloadAction<Target>) => {
            const target: Target = action.payload;
            state.selectedColors = state.selectedColors.filter(c => c !== state[target].secret);
            state[target].hasPicked = false;
            state[target].secret = initialColor;
            propagateUpdates(state, target);
        },
        // show generator of target
        showGenerator: (state, action: PayloadAction<Target>) => {
            const target: Target = action.payload;
            state.msgProgress.generatorShown[target] = true;
        },
        hasOtherMix: (state, action: PayloadAction<Target>) => {
            const target: Target = action.payload;
            state.msgProgress.hasOtherMix[target] = true;
        },
        showPopup: (state, action: PayloadAction<{
            title: string;
            icon: React.ReactNode;
            children: React.ReactNode;
            timerDuration?: number;
        }>) => {
            state.msgProgress.popup = action.payload;
        },
        hidePopup: (state) => {
            state.msgProgress.popup = null;
        },

        // FOR BOTH -------------------------------

        reset: (state) => {
            return { // as new object => recognized as change
                ...initialMixState,
            };
        },
        // new 'Gemeinsame Farbe'
        generate: (state, action: PayloadAction<string | null | undefined>) => {
            const selectedColor = action.payload;
            const newGenColor = selectedColor ?? getRandFreeColor(COLORS, state.selectedColors);

            // remove old, add new generator color to selectedColors
            state.selectedColors = state.selectedColors.filter(c => c !== state.generator);
            state.generator = newGenColor;
            state.selectedColors.push(state.generator);

            propagateUpdates(state);
        },
        // starts for {A|B}
        start: (state, action: PayloadAction<Target>) => {
            const target = action.payload;
            state[target].hasStarted = true;
        },
        // saves '{A|B} Geheim'
        pick: (state, action: PayloadAction<{ target: Target, color: string }>) => {
            const { target, color } = action.payload;
            if (!state.selectedColors.includes(action.payload.color)) { // only if not already picked
                state.selectedColors = state.selectedColors.filter(c => c !== state[target].secret); // removes old...
                state.selectedColors.push(action.payload.color); // ... and adds new secret

                state[target].secret = color;

                propagateUpdates(state, target); // falls neu picked
                state[target].hasPicked = true; // bei neu picked eh schon true
            }
        },
        // 'Gemeinsame Farbe' + '{A|B} Geheim'
        mix: (state, action: PayloadAction<Target>) => {
            const target = action.payload;
            state[target].mixed = mixColors([state.generator, state[target].secret])
            state[target].hasMixed = true;
        },
        send: (state, action: PayloadAction<Target>) => {
            const target = action.payload;
            const other = target === "bob" ? "alice" : "bob";
            state[target].hasSent = true;
            state[other].hasReceived = true;
            state[other].received = state[target].mixed;
        },
        // calcs 'Geteilt Geheim' for {A|B}
        finish: (state, action: PayloadAction<Target>) => {
            const target = action.payload;
            state[target].hasResult = true;
            state[target].result = mixColors([
                state.generator,
                state.bob.secret,
                state.alice.secret,
            ]);
        }
    },
});

export const mixActions = mixSlice.actions;
export default mixSlice.reducer;
